/**
 * What every screen in this product is for, in one place.
 *
 * Field Pulse has grown to ten groups and forty-odd screens. Somebody opening
 * "Putaway", "Pipe Tally", "Charge Types" or "PO Visibility" for the first time
 * has no way to know what those are — the label names the noun and nothing
 * explains the job. This registry carries that explanation once, and three
 * consumers read it, so the rail, the page header and the guide cannot disagree
 * about what a screen is:
 *
 *   - the navigation rail            (one line under each item)
 *   - the page header                (ModuleIntro: purpose, and the flow either side)
 *   - the guide at /guide            (the whole map)
 *
 * Two rules held while writing it. Every `purpose` is written in the language a
 * yard supervisor or a planner would use, never in the language of the schema —
 * "the pipe you have, joint by joint", not "tubular consignment register". And
 * every `question` is the thing somebody actually opens the screen to find out,
 * because a screen that cannot name its question usually should not exist.
 */

export type ModuleTerm = {
  term: string;
  /** Plain-language meaning, oilfield first and software second. */
  means: string;
};

export type ModuleEntry = {
  /** Route path. The key everything else joins on. */
  path: string;
  title: string;
  /** One line: what this screen is. */
  purpose: string;
  /** The question somebody opens it to answer. */
  question: string;
  /** Screens whose output lands here. Paths, so the intro can link them. */
  upstream?: string[];
  /** Screens that consume what happens here. */
  downstream?: string[];
  /** Vocabulary this screen assumes the reader already has. */
  terms?: ModuleTerm[];
  /**
   * Reachable and explained, but kept off the rail. Sub-screens of a module
   * that would crowd it — rate imports, variance breakdowns — still need a
   * definition when somebody lands on them from a link.
   */
  hidden?: boolean;
};

export type NavGroup = {
  /** Short code used in the guide and in analytics. */
  code: string;
  title: string;
  /** What the whole group is for — shown when the group is expanded. */
  blurb: string;
  /** lucide icon name, resolved by the layout. */
  icon: string;
  /** Permission gating the whole group. */
  permission?: string;
  /**
   * Hide the group from anyone who *has* this permission.
   *
   * One group needs it: the customer portal. Gating it on `portal:read` alone is
   * not enough, because an internal admin holds `*:*` and therefore holds every
   * permission there is — so the customer's own view appeared in the staff rail,
   * beside the internal consignment screens and confusingly sharing their name.
   *
   * The rule is the same one the landing route uses: portal, and not internal.
   */
  excludePermission?: string;
  children: ModuleEntry[];
};

/**
 * Groups are ordered along the work itself rather than alphabetically or by
 * how the modules were built: what needs a human now, what we have been asked
 * to do, what we hold, where it sits, what carries it, what pays for it, what
 * permits it, and then setup. A reader going down the rail is walking the
 * operation in order.
 */
export const NAV_GROUPS: NavGroup[] = [
  /**
   * The customer's own view. Gated on `portal:read`, which only a customer role
   * carries — and because the rail hides any group whose permission is missing,
   * a customer signing in sees this and nothing else, while nobody internal sees
   * it at all.
   */
  {
    code: 'CUS',
    title: 'Consignment',
    blurb: 'Stock held at the site under a consignment contract, and the call-offs raised against it.',
    icon: 'Boxes',
    permission: 'portal:read',
    // Not for staff: they have the internal consignment screens, which show the
    // same stock from the supplier's side and with the money on.
    excludePermission: 'inventory:read',
    children: [
      {
        path: '/portal',
        title: 'Your stock',
        purpose: 'Stock standing at the site, split between what the supplier still owns and what title has passed on.',
        question: 'What can I draw on today, and where has my request got to?',
        terms: [
          {
            term: 'Consignment',
            means:
              'Your supplier keeps material at your site and owns it until you draw it. You pay for what you take, not for what stands there.',
          },
          {
            term: 'Available to draw',
            means:
              'Standing at your site, still your supplier\u2019s, and not promised to anything else.',
          },
          {
            term: 'Already yours',
            means:
              'You have drawn it and title has passed, but it has not physically left the site yet.',
          },
        ],
      },
    ],
  },
  {
    code: 'CMD',
    title: 'Command',
    blurb: 'Open exceptions gathered from every module, ordered by what needs attention first.',
    icon: 'LayoutDashboard',
    /**
     * Gated, where it used to be open to anyone signed in.
     *
     * That was harmless while every login was an employee. It stopped being so
     * with the customer portal: a customer has none of these permissions, so the
     * pages refuse them correctly, but the rail was still offering four screens
     * that answer with 403. `analytics:read` is what the dashboard, analytics and
     * the exceptions feed all require, so it is the honest gate rather than a
     * convenient one.
     */
    permission: 'analytics:read',
    children: [
      {
        path: '/',
        title: 'Dashboard',
        purpose:
          'Stock, orders, shipments and open alerts, on one screen.',
        question: 'What is the shape of things this morning?',
        downstream: ['/approvals'],
      },
      {
        path: '/approvals',
        title: 'Approvals',
        purpose:
          'Items awaiting a decision, across consumption, permits, RFQs and cycle counts.',
        question: 'What is blocked on my signature?',
        upstream: ['/consumption', '/permits', '/tms/rfq', '/inventory/cycle-counts'],
      },
      {
        path: '/analytics',
        title: 'Analytics',
        purpose: 'Cross-module reporting on spend, movement, stock turns and service performance.',
        question: 'What do the numbers say over a quarter rather than today?',
      },
      {
        path: '/alerts',
        title: 'Alerts',
        purpose: 'Conditions the system has noticed and nobody has cleared yet.',
        question: 'What has gone wrong that I have not seen?',
      },
    ],
  },
  {
    code: 'WRK',
    title: 'Work',
    blurb:
      'Field service and maintenance. A job is one service at one well for one client; a work order is maintenance on owned equipment.',
    icon: 'Drill',
    permission: 'consumption:read',
    children: [
      {
        path: '/jobs',
        title: 'Jobs',
        purpose:
          'Each service running at a well, with the rig, the AFE and the cost to date.',
        question: 'What are we running, where, and is it eating its budget?',
        downstream: ['/consumption', '/yard/job-kits'],
        terms: [
          {
            term: 'AFE',
            means:
              "The client's Authorisation For Expenditure — their budget code, which our charges are billed against.",
          },
          {
            term: 'Base metric',
            means:
              'The work the job is planned to do — 9,200 ft to drill, 40 stages to pump. Consumption is measured per unit of it.',
          },
        ],
      },
      {
        path: '/wells',
        title: 'Wells',
        purpose:
          'Every well on the books, and what has been consumed across the jobs performed on it.',
        question: 'What has this well cost us across drilling, completion and everything since?',
        downstream: ['/jobs'],
        terms: [
          {
            term: 'API number',
            means:
              'The regulatory well identifier. The one number we, the operator and the state all agree on — our well name and theirs routinely differ.',
          },
          {
            term: 'Operator',
            means:
              'The company that owns the well. We are the service company performing work on it; billing is settled in their ERP.',
          },
        ],
      },
      {
        path: '/consumption',
        title: 'Consumption',
        purpose:
          'Material drawn against a job, how it ran against plan, and the variances awaiting a decision.',
        question: 'What did this job actually burn, and why was it more than we said?',
        upstream: ['/jobs', '/inventory'],
        downstream: ['/forecasting', '/approvals'],
        terms: [
          {
            term: 'Variance',
            means:
              'Actual against planned. Over-consumption costs money; under-consumption means the plan was wrong. Both are worth knowing.',
          },
          {
            term: 'Backload',
            means:
              'Material returned unused. Net consumption is what was drawn minus what came back.',
          },
        ],
      },
      // Variance and approvals became tabs on /consumption rather than pages of
      // their own. The paths stay listed so old links and bookmarks resolve —
      // they redirect onto the right tab — and so the guide can still explain
      // what each one answers.
      {
        path: '/consumption/variance',
        title: 'Against plan',
        purpose:
          'Consumption variance aggregated by item rather than by record. A tab on Consumption.',
        question: 'Which items consistently run over plan?',
        hidden: true,
      },
      {
        path: '/consumption/approvals',
        title: 'Approvals',
        purpose:
          'Variances waiting on somebody to accept or reject the overrun. A tab on Consumption.',
        question: 'Which draws need a decision?',
        hidden: true,
      },
      {
        path: '/work-orders',
        title: 'Work Orders',
        purpose:
          'Maintenance and repair raised against owned equipment, with the parts each will consume.',
        question: 'What is being worked on, by whom, and is it waiting for parts?',
        upstream: ['/maintenance', '/equipment'],
        downstream: ['/inventory'],
      },
      {
        path: '/maintenance',
        title: 'Maintenance Schedules',
        purpose: 'The cadence each asset is serviced on, and what falls due next.',
        question: 'What is due, what is overdue, and what will it need?',
        downstream: ['/work-orders'],
        terms: [
          {
            term: 'Condition-based',
            means:
              'Triggered by a meter reading — run hours, cycles — rather than by the calendar.',
          },
        ],
      },
    ],
  },
  {
    code: 'EQP',
    title: 'Equipment',
    blurb:
      'Owned assets, where they are, what condition they are in, and the case for repairing or replacing them.',
    icon: 'Wrench',
    permission: 'equipment:read',
    children: [
      {
        path: '/equipment',
        title: 'All Assets',
        purpose:
          'Every serialised asset with its status, condition, location and service position.',
        question: 'Is this tool fit to go to a well next week?',
        downstream: ['/work-orders', '/certificates'],
      },
      {
        path: '/equipment/hierarchy',
        title: 'Hierarchy',
        purpose: 'How equipment is assembled, from rig down through subsystem to component.',
        question: 'What does this failure belong to, and what did that subsystem cost us?',
      },
      {
        path: '/equipment/certification-gaps',
        title: 'Certification Gaps',
        purpose: 'Assets missing, or about to lose, a certificate their class says they must hold.',
        question: 'What are we about to send to a well without its paperwork?',
        upstream: ['/equipment', '/certificates'],
        terms: [
          {
            term: 'Required certificate type',
            means:
              "Declared on the asset's class, not the asset. Without that declaration a missing certificate is invisible — you can only ever see the ones that exist.",
          },
          {
            term: 'Lapsed versus never obtained',
            means:
              'A lapsed certificate can be renewed from a known starting point. One that was never obtained means nobody has ever assessed the asset.',
          },
        ],
      },
      {
        path: '/equipment/out-on-jobs',
        title: 'Out on Jobs',
        purpose:
          'Assets issued to a job and not yet returned, ranked by days overdue, including those with no return date recorded.',
        question: 'What have we got at a wellsite that should have come home?',
        upstream: ['/jobs', '/equipment'],
        terms: [
          {
            term: 'Lost in hole',
            means:
              'Downhole equipment that cannot be recovered. Recoverable from the operator when it is documented at the time, absorbed when it is not — so it is recorded here while the job is still open.',
          },
          {
            term: 'No return date',
            means:
              'Out, with nobody having said when it is due. It cannot be reported late, which is how an asset sits in a yard for weeks unnoticed.',
          },
        ],
      },
      {
        path: '/equipment/rentals',
        title: 'Rentals',
        purpose:
          'Equipment hired from third parties, with contracted return dates and the cost still accruing on overdue units.',
        question: 'What are we still paying a day rate for that we no longer need?',
        upstream: ['/jobs'],
        terms: [
          {
            term: 'Off-hire',
            means:
              'The day a rental physically goes back to the lessor and stops accruing. The hire itself is raised and settled in the ERP; this records the date and reminds somebody to act on it.',
          },
          {
            term: 'Job finished',
            means:
              'Still on hire after the job it came in for ended. The thing an ERP cannot see, and the reason this screen exists.',
          },
        ],
      },
      {
        path: '/equipment/rotable-assets',
        title: 'Rotable Assets',
        purpose:
          'Repairable assets that cycle through a pool, with the arithmetic behind repair or replace.',
        question: 'Have we spent more on this pump than a new one costs?',
        terms: [
          {
            term: 'Rotable',
            means:
              'A repairable asset that cycles: installed, removed, repaired, tested, returned to pool, installed again.',
          },
          {
            term: 'Cycle',
            means: 'One trip through that loop. Each one consumes life as well as money.',
          },
        ],
      },
    ],
  },
  {
    code: 'MAT',
    title: 'Materials',
    blurb: 'Stock on hand and what it is worth, what is running short, and what demand is expected next.',
    icon: 'Package',
    permission: 'inventory:read',
    children: [
      {
        path: '/inventory',
        title: 'All Items',
        purpose: 'Every stock item with what is on hand, on order, and how long the cover lasts.',
        question: 'Do we have it, where, and for how much longer?',
        downstream: ['/consumption', '/purchase-orders'],
        terms: [
          {
            term: 'Days of stock',
            means:
              'Cover on hand divided by the daily rate it leaves at. The number a planner reorders on.',
          },
          {
            term: 'ABC class',
            means:
              'Value class — how much of the money this item represents. It drives counting frequency, not urgency.',
          },
        ],
      },
      {
        path: '/inventory/critical-spares',
        title: 'Critical Spares',
        purpose: 'Parts ranked by what their absence would stop, not by what they cost.',
        question: 'Which cheap part could stop a rig?',
        upstream: ['/equipment'],
        terms: [
          {
            term: 'Criticality',
            means:
              'Operational consequence. A $400 seal kit can be rig-stopping; a value class will never say so.',
          },
          {
            term: 'MTBF',
            means: 'Mean time between failures — how often this part fails in service.',
          },
          {
            term: 'Criticality score',
            means:
              'Equipment criticality times downtime cost, divided by MTBF times lead time. High means a part that fails often, takes a long time to replace, and stops something expensive when it does.',
          },
          {
            term: 'Not assessed',
            means:
              'Nobody has recorded what a stock-out here would cost. Not the same as low — an unjudged part is a decision outstanding, and it is ranked as one.',
          },
        ],
      },
      {
        path: '/consignment/call-offs/:id',
        title: 'Call-off',
        purpose: 'One draw against stock held at a customer\u2019s site, and what happened to it.',
        question: 'What was drawn, by whom, and has it left our position yet?',
        hidden: true,
      },
      {
        path: '/consignment',
        title: 'Consignment',
        purpose: 'Stock standing at a customer\u2019s site, and the call-offs drawn against it.',
        question: 'What of ours is sitting at the customer, and how much is still ours?',
        // Not Consumption. A call-off is the customer taking title to material
        // they are buying; consumption is our own crews using our own stock. The
        // glossary two entries below says exactly that, and the guide was drawing
        // an arrow that contradicted it.
        //
        // The true loop: our stock, despatched to the node by replenishment,
        // drawn down by call-off. Inventory feeds it; shipments carry what it asks
        // for.
        upstream: ['/inventory'],
        downstream: ['/shipments'],
        terms: [
          {
            term: 'Consignment node',
            means:
              'A customer\u2019s site where we keep stock under contract. It is their yard; the material is ours until they draw it.',
          },
          {
            term: 'Call-off',
            means:
              'The customer taking stock from what we hold at their site. The moment title passes, which is the moment it becomes billable \u2014 not a purchase order, because nothing is being bought in.',
          },
          {
            term: 'Title only',
            means:
              'A call-off where ownership passes but the material stays standing at the node. It leaves our position without leaving the site.',
          },
          {
            term: 'Free to call off',
            means:
              'What is there, less what the customer already owns and what is reserved for something else.',
          },
        ],
      },
      {
        path: '/inventory/cycle-counts',
        title: 'Cycle Counts',
        purpose:
          'Scheduled physical counts, their variances, and the approval before any adjustment posts.',
        question: 'Does the shelf agree with the system?',
        downstream: ['/approvals'],
        terms: [
          {
            term: 'Variance threshold',
            means: 'The gap beyond which a count cannot post without somebody approving it.',
          },
        ],
      },
      {
        path: '/inventory/site-optimization',
        title: 'Site Optimisation',
        purpose: 'Where stock is in the wrong place, and what moving it would cost.',
        question: 'Which site is short of something another site is sitting on?',
        upstream: ['/inventory'],
        downstream: ['/shipments'],
      },
      {
        path: '/forecasting',
        title: 'Forecasting',
        purpose: 'Expected demand per item and location, with how well past forecasts held up.',
        question: 'How much will we need, and can I trust the number?',
        upstream: ['/consumption'],
        downstream: ['/purchase-orders'],
        terms: [
          {
            term: 'MAPE',
            means:
              'Mean absolute percentage error — how far past forecasts missed. Past about 20% the forecast is not worth planning to.',
          },
        ],
      },
      {
        path: '/risk',
        title: 'Supply Risk',
        purpose:
          'Risk per item across sourcing, demand, quality, price and obsolescence, with the mitigation recorded against each.',
        question: 'What could stop us getting this, and what are we doing about it?',
      },
      {
        path: '/quick-scan',
        title: 'Quick Scan',
        purpose: 'Barcode lookup for any item, asset or bin in the yard.',
        question: 'What is this thing in my hand?',
      },
    ],
  },
  {
    code: 'YRD',
    title: 'Yard',
    blurb:
      'Where stock physically sits between jobs, down to the bin. Pipe is counted joint by joint rather than by quantity.',
    icon: 'Warehouse',
    permission: 'inventory:read',
    children: [
      {
        path: '/yard',
        title: 'Yard Overview',
        purpose: 'Occupancy and activity across the yard, by zone.',
        question: 'How full are we, and where is the work?',
      },
      {
        path: '/yard/locations',
        title: 'Racks & Bins',
        purpose:
          'Addressable storage positions from zone down to bin, and what each one is able to hold.',
        question: 'Where exactly does this go, and will it fit?',
      },
      {
        path: '/yard/putaway',
        title: 'Putaway',
        purpose: 'Moving received goods out of the receiving bay and into an addressable position.',
        question: 'What has arrived but is not yet findable?',
        upstream: ['/goods-receipt'],
        terms: [
          {
            term: 'Putaway',
            means:
              'Received is not the same as put away. Stock in the receiving bay is on site and not addressable — the state a yard loses track of.',
          },
        ],
      },
      {
        path: '/yard/picking',
        title: 'Picking',
        purpose: 'Pick lists worked through in walking order, with what was short.',
        question: 'What is being pulled, by whom, and did it all come off the shelf?',
        downstream: ['/yard/job-kits'],
        terms: [
          {
            term: 'Short pick',
            means:
              'Less came off the shelf than the list asked for. Somebody has to know before the truck leaves.',
          },
        ],
      },
      {
        path: '/yard/job-kits',
        title: 'Job Kits',
        purpose: 'Tools and materials gathered for one job, issued out and reconciled on return.',
        question: 'Is the kit for this well complete, and did it all come back?',
        upstream: ['/jobs', '/yard/picking'],
      },
      {
        path: '/yard/pipe-tally',
        title: 'Pipe Tally',
        purpose:
          'Tubulars counted joint by joint, with measured length, grade, heat number and inspected class.',
        question: 'How much usable pipe do we actually have, at what class?',
        terms: [
          {
            term: 'Tally',
            means: 'The running sum of measured joint lengths. Pipe is footage, not a count.',
          },
          {
            term: 'Heat number',
            means:
              'The batch of steel a joint was poured from. If a joint fails downhole, this is the first question.',
          },
          {
            term: 'Downgraded',
            means:
              'Sound pipe inspected below the class it was ordered at. Not scrap, and not usable in a string specified at the higher class.',
          },
        ],
      },
      {
        path: '/yard/asset-search',
        title: 'Asset Search',
        purpose: 'Find anything in the yard across items, assets, kits and pipe at once.',
        question: 'Where is it?',
      },
    ],
  },
  {
    code: 'MOV',
    title: 'Movement',
    blurb:
      'Freight out to the wellsite and back again. Returning equipment accounts for roughly half of oilfield movements.',
    icon: 'Truck',
    permission: 'shipment:read',
    children: [
      {
        path: '/tms',
        title: 'Transport Overview',
        purpose: 'Volumes, spend, carrier mix and open exceptions across every movement.',
        question: 'How is freight performing overall?',
      },
      {
        path: '/shipments',
        title: 'Shipments',
        purpose: 'Consignments in flight, with what is on them and when they are expected.',
        question: 'Where is it, and will it arrive before the rig needs it?',
        downstream: ['/goods-receipt'],
      },
      {
        path: '/tms/load-builder',
        title: 'Load Builder',
        purpose: 'Putting consignments onto vehicles without exceeding weight or volume.',
        question: 'What fits on this truck?',
      },
      {
        path: '/tms/track-trace',
        title: 'Track & Trace',
        purpose: 'Live position, milestones, predicted arrival and any demurrage building up.',
        question: 'Has it moved, and is the clock running against us?',
        terms: [
          {
            term: 'Demurrage',
            means:
              'Charges once a container sits beyond its free time. It accrues silently and the invoice arrives months later.',
          },
          { term: 'Free time', means: 'The days after discharge before demurrage starts.' },
        ],
      },
      {
        path: '/shipment-planner',
        title: 'Shipment Planner',
        purpose: 'Building consignments from demand before they become shipments.',
        question: 'What should ship together, and when?',
        hidden: true,
      },
    ],
  },
  {
    code: 'SUP',
    title: 'Supply',
    blurb:
      'Procurement from order through to receipt, and how reliably each supplier delivers.',
    icon: 'ShoppingCart',
    permission: 'purchase_order:read',
    children: [
      {
        path: '/purchase-orders',
        title: 'Purchase Orders',
        purpose: 'Orders placed with suppliers, and the dates each one is promised against.',
        question: 'Is the thing we are short of already on order?',
        upstream: ['/inventory', '/forecasting'],
        downstream: ['/goods-receipt'],
      },
      {
        path: '/goods-receipt',
        title: 'Goods Receipt',
        purpose: 'What arrived against an order, what was accepted, and what was wrong with it.',
        question: 'Did we get what we paid for?',
        upstream: ['/purchase-orders', '/shipments'],
        downstream: ['/yard/putaway'],
        terms: [
          {
            term: 'Quarantine',
            means:
              'Received into the building but held — physically present and operationally absent.',
          },
          {
            term: 'Discrepancy',
            means:
              'A quantity, quality or documentation problem raised against a receipt. Without evidence filed, it is money you will not recover.',
          },
        ],
      },
      {
        path: '/po-visibility',
        title: 'PO Visibility',
        purpose: 'Where each order has got to, from placement through to receipt.',
        question: 'Why has this order not landed?',
        upstream: ['/purchase-orders'],
      },
      {
        path: '/supply-analytics',
        title: 'Supply Analytics',
        purpose: 'Where the lead time goes, what is being paid to hurry, and what freight costs against the goods.',
        question: 'Which part of the wait is the slow one, and what is the freight really costing us?',
        upstream: ['/purchase-orders', '/shipments', '/goods-receipt'],
        terms: [
          {
            term: 'Staged lead time',
            means:
              'The wait split into making, moving and clearing. Three suppliers can average fifty days and be slow in three different places.',
          },
          {
            term: 'Hot shot',
            means:
              'A run paid a premium to make a date, usually dedicated road. It is a decision, not a transport mode \u2014 air freight can be somebody\u2019s routine lane.',
          },
          {
            term: 'Freight vs goods',
            means:
              'Freight apportioned to an item against what the item is worth. A cheap heavy part can cost more to move than to buy, and nothing on a purchase order says so.',
          },
        ],
      },
      {
        path: '/suppliers',
        title: 'Suppliers',
        purpose: 'Suppliers on contract, and how each scores on delivery and quality.',
        question: 'Can we rely on them for the next order?',
      },
    ],
  },
  {
    code: 'SRC',
    title: 'Freight Sourcing',
    blurb: 'Carriers under contract, the rates they hold, and the sourcing that agreed them.',
    icon: 'Handshake',
    permission: 'tms:read',
    children: [
      {
        path: '/tms/carriers',
        title: 'Carriers',
        purpose:
          'Hauliers and forwarders under contract, the lanes they cover, and how each has performed.',
        question: 'Who can move this, and are they any good?',
        downstream: ['/tms/rfq'],
      },
      {
        path: '/tms/rfq',
        title: 'Freight RFQ',
        purpose: 'Requests for quote sent to carriers, the responses, and the award.',
        question: 'What will this lane cost, and who should get it?',
        upstream: ['/tms/carriers'],
        downstream: ['/tms/rates'],
        terms: [
          {
            term: 'Lane',
            means:
              'An origin-destination pair at a given mode. Rates are quoted per lane, not per shipment.',
          },
        ],
      },
      {
        path: '/tms/rfq-approvals',
        title: 'RFQ Approvals',
        purpose: 'Awards waiting on the approval chain before they become rates.',
        question: 'What am I holding up?',
        upstream: ['/tms/rfq'],
      },
      {
        path: '/tms/rates',
        title: 'Rates',
        purpose: 'Agreed rate cards by lane and mode, with their validity windows.',
        question: 'What are we contracted to pay for this lane today?',
        upstream: ['/tms/rfq'],
      },
      {
        path: '/tms/rate-templates',
        title: 'Rate Templates',
        purpose: 'Reusable rate structures so a new agreement does not start from a blank sheet.',
        question: 'What shape should this rate card take?',
        hidden: true,
      },
      {
        path: '/tms/rate-import',
        title: 'Rate Import',
        purpose: 'Bulk load of carrier rate cards from a spreadsheet.',
        question: 'How do I get 400 lanes in without typing them?',
        hidden: true,
      },
      {
        path: '/tms/rate-dashboard',
        title: 'Rate Dashboard',
        purpose: 'Coverage and expiry across all rate cards.',
        question: 'Which lanes have no valid rate?',
        hidden: true,
      },
      {
        path: '/tms/rate-analytics',
        title: 'Rate Analytics',
        purpose: 'How rates compare across carriers and how they have moved.',
        question: 'Are we paying market?',
        hidden: true,
      },
      {
        path: '/tms/charge-types',
        title: 'Charge Types',
        purpose: 'Named surcharges a rate can carry, such as fuel, waiting time, demurrage and escort.',
        question: 'What is this line on the invoice?',
      },
      {
        path: '/tms/vendor-groups',
        title: 'Vendor Groups',
        purpose:
          'Sets of carriers invited together, so an RFQ can go to a panel rather than one name at a time.',
        question: 'Who should this go out to?',
      },
      {
        path: '/tms/terms',
        title: 'Terms & Conditions',
        purpose: 'Reusable contract terms attached to rate agreements.',
        question: 'What did we agree beyond the price?',
      },
      {
        path: '/tms/rfq-analytics',
        title: 'RFQ Analytics',
        purpose: 'Sourcing performance by response rate, saving achieved and cycle time.',
        question: 'Is our sourcing process working?',
      },
    ],
  },
  {
    code: 'CMP',
    // "Governance" until the navigation review. The blurb below was already
    // right — it covers a customs permit and a mill certificate equally well —
    // so the grouping was sound and only the label was wrong. Governance means
    // data or corporate governance in most enterprise software; what this holds
    // is compliance.
    title: 'Compliance',
    blurb:
      'Whether an asset is permitted to move or be used, and the certificates and customs cases that decide it.',
    icon: 'ShieldCheck',
    permission: 'compliance:read',
    children: [
      {
        path: '/permits',
        title: 'Temporary Permits',
        purpose:
          'Customs cases admitting equipment into a country against an undertaking that it leaves again.',
        question: 'Is this equipment legally in the country, and until when?',
        downstream: ['/permits/watchlist'],
        terms: [
          {
            term: 'Temporary import',
            means:
              'Duty is suspended on condition the goods leave by a stated date. Miss it and the guarantee is forfeited.',
          },
          {
            term: 'Security instrument',
            means:
              'A bank guarantee or cash deposit lodged with the customs authority — real money, recoverable only once the case closes.',
          },
        ],
      },
      {
        path: '/permits/watchlist',
        title: 'Expiry Watchlist',
        purpose: 'Obligations coming due, banded by how soon they bite.',
        question: 'What must leave the country before we lose the deposit?',
        upstream: ['/permits'],
      },
      {
        path: '/permits/countries',
        title: 'Permit Countries',
        purpose:
          'Customs rules by country, covering the authority chain, the renewal model and what counts as proof of exit.',
        question: 'How does this country work?',
      },
      {
        path: '/certificates',
        title: 'Certificates',
        purpose:
          'Inspection, pressure test and material certificates, with what they cover and when they lapse.',
        question: 'Is this asset certified to go to a well?',
        upstream: ['/equipment'],
        terms: [
          {
            term: 'Heat number',
            means:
              'On a mill certificate, the batch of steel the material was poured from — the link between a certificate and a specific joint of pipe.',
          },
        ],
      },
      {
        path: '/compliance',
        title: 'Compliance',
        // Merged into /certificates, which listed the same records through a
        // different fetch. The path stays listed so old links resolve and the
        // guide can still explain what it used to answer.
        hidden: true,
        purpose:
          'Material certification coverage and the gaps in it. Now a redirect to Certificates.',
        question: 'What are we running without paperwork?',
      },
    ],
  },
  {
    code: 'ADM',
    title: 'Setup',
    blurb: 'Company records, who may see what, and the sites the operation runs from.',
    icon: 'Settings',
    permission: 'company:read',
    children: [
      {
        path: '/locations',
        title: 'Sites & Hubs',
        purpose: 'Bases, depots and yards across the network, and the stock each one holds.',
        question: 'Where do we operate from?',
      },
      {
        path: '/admin/companies',
        title: 'Companies',
        purpose: 'The legal entities on the platform and their relationships.',
        question: 'Which entity does this belong to?',
      },
      {
        path: '/admin/users',
        title: 'Users',
        purpose: 'People with access, and what they are entitled to see.',
        question: 'Who can get in?',
      },
      {
        path: '/admin/roles',
        title: 'Roles & Permissions',
        purpose: 'What each role may read and change.',
        question: 'Why can this person not see that screen?',
      },
      {
        path: '/admin/partners',
        title: 'Partner Networks',
        purpose: 'Suppliers, carriers and customers granted scoped access to the platform.',
        question: 'What can an outsider see?',
      },
      {
        path: '/admin/audit-logs',
        title: 'Audit Logs',
        purpose: 'Every access decision, with who requested what and whether it was permitted.',
        question: 'Who looked at this, and when?',
      },
    ],
  },
];

/** Flat index by path, built once. */
export const MODULE_BY_PATH: Record<string, ModuleEntry> = Object.fromEntries(
  NAV_GROUPS.flatMap((g) => g.children.map((c) => [c.path, c]))
);

/** The group a path belongs to. */
export const GROUP_BY_PATH: Record<string, NavGroup> = Object.fromEntries(
  NAV_GROUPS.flatMap((g) => g.children.map((c) => [c.path, g]))
);

/**
 * Resolve a browser pathname to its module.
 *
 * Longest match wins, so `/inventory/cycle-counts` resolves to cycle counts
 * rather than to All Items, and a detail route like `/jobs/abc123` resolves to
 * the list it belongs to.
 */
export function moduleForPath(pathname: string): ModuleEntry | undefined {
  if (MODULE_BY_PATH[pathname]) return MODULE_BY_PATH[pathname];
  const candidates = Object.keys(MODULE_BY_PATH)
    .filter((p) => p !== '/' && (pathname === p || pathname.startsWith(p + '/')))
    .sort((a, b) => b.length - a.length);
  return candidates[0] ? MODULE_BY_PATH[candidates[0]] : undefined;
}

export function groupForPath(pathname: string): NavGroup | undefined {
  const entry = moduleForPath(pathname);
  return entry ? GROUP_BY_PATH[entry.path] : undefined;
}
